import React from "react";
import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box, Paper, Typography, TextField, CircularProgress } from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import List from "@mui/material/List";
import { ApiGet, ApiPost, ApiPut, ApiDelete } from "../helpers/API/ApiData";
import { toast, ToastContainer } from "react-toastify";
import { DataTable } from "../components/DataTable";
import moment from "moment";
import { usePermissions } from "../hooks/usePermissions";
import { PageHeader } from "../components/PageHeader";
import { buildWarningColumns } from "./warning/warningColumns";
import { useContentLanguage } from "../hooks/useContentLanguage";
import { LanguageTabs } from "../components/LanguageTabs/LanguageTabs";

export default function Warning() {
  const { canCreate, canEdit, canDelete } = usePermissions("/warning");
  const [lang, setLang] = useContentLanguage();
  const [warningData, setWarningData] = useState<any>({});
  const [addWarning, setAddWarning] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [getAllWarning, setGetAllWarning] = useState<any[]>([]);
  const [isEditApi, setIsEditApi] = useState(false);
  const [search, setSearch] = useState<any>();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [warningIdForDelete, setWarningIdForDelete] = useState<any>();
  const [warningIdForEdit, setWarningIdForEdit] = useState<any>();
  const [countPerPage, setCountPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const [loaderForGetAll, setLoaderForGetAll] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    getAllWarningData();
  }, [page, countPerPage, search]);

  const addCompanyModelClose = () => {
    setAddWarning(false);
    setIsEditApi(false);
    setErrors({});
    setWarningData({});
  };

  const onhandleChange = (e: any) => {
    const { name, value } = e.target;
    setWarningData({ ...warningData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const formValidation = () => {
    let formIsValid = true;
    const errs: any = {};
    if (!warningData?.warningName?.trim()) {
      formIsValid = false;
      errs["warningName"] = "*Please enter the English title";
    }
    if (!warningData?.warningName_ger?.trim()) {
      formIsValid = false;
      errs["warningName_ger"] = "*Bitte geben Sie den deutschen Titel ein";
    }
    if (!warningData?.warningDetails?.trim()) {
      formIsValid = false;
      errs["warningDetails"] = "*Please enter the English warning details";
    }
    if (!warningData?.warningDetails_ger?.trim()) {
      formIsValid = false;
      errs["warningDetails_ger"] = "*Bitte geben Sie die deutschen Warndetails ein";
    }
    if (!warningData?.date) {
      formIsValid = false;
      errs["date"] = "*Bitte geben Sie ein g\u00fcltiges Datum ein";
    }
    setErrors(errs);
    return formIsValid;
  };

  const handleClose = () => {
    setShow(false);
  };

  const getAllWarningData = async () => {
    setLoaderForGetAll(true);
    const searchParam = search ? `&letter=${search}` : "";
    await ApiGet(`warning/find?page=${page}&limit=${countPerPage}${searchParam}`)
      .then((res: any) => {
        setGetAllWarning(res?.data?.data);
        setCount(res?.data?.count ?? 0);
        setLoaderForGetAll(false);
      })
      .catch(() => {
        setLoaderForGetAll(false);
      });
  };

  const buildPayload = () => ({
    warningName: warningData?.warningName,
    warningName_ger: warningData?.warningName_ger,
    warningDetails: warningData?.warningDetails,
    warningDetails_ger: warningData?.warningDetails_ger,
    date: warningData?.date,
    expiryDate: warningData?.expiryDate || "",
  });

  const addWarningData = async () => {
    if (formValidation()) {
      setLoader(true);
      await ApiPost(`warning/create`, buildPayload())
        .then(() => {
          toast.success("Warnung erfolgreich hinzuf\u00fcgen");
          setAddWarning(false);
          setIsEditApi(false);
          getAllWarningData();
          setWarningData({});
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  const editWarningData = async () => {
    if (formValidation()) {
      setLoader(true);
      await ApiPut(`warning/update?id=${warningIdForEdit}`, buildPayload())
        .then(() => {
          toast.success("Warnung erfolgreich aktualisiert");
          getAllWarningData();
          setAddWarning(false);
          setIsEditApi(false);
          setWarningData({});
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  const deleteWarningData = async () => {
    await ApiDelete(`warning/delete?id=${warningIdForDelete}`)
      .then((res: any) => {
        if (res?.status === 200) {
          toast.success("Warnung erfolgreich gel\u00f6scht");
          setShow(false);
          setPage(1);
          getAllWarningData();
        } else {
          toast.error(res?.data?.message || "Etwas ist schief gelaufen...");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      });
  };

  const columns = useMemo(() => buildWarningColumns({
    canEdit,
    canDelete,
    lang,
    onEdit: (row: any) => {
      setIsEditApi(true);
      setAddWarning(true);
      setWarningIdForEdit(row?._id);
      setWarningData({
        warningName: row?.warningName,
        warningName_ger: row?.warningName_ger,
        warningDetails: row?.warningDetails,
        warningDetails_ger: row?.warningDetails_ger,
        date: moment(row?.date).format("YYYY-MM-DDTHH:mm"),
        expiryDate: row?.expiryDate ? moment(row?.expiryDate).format("YYYY-MM-DDTHH:mm") : "",
      });
    },
    onDelete: (row: any) => {
      setWarningIdForDelete(row?._id);
      setShow(true);
    },
  }), [canEdit, canDelete, lang]);

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <ToastContainer />
        <PageHeader
          title="Warnungen"
          onSearch={(val) => { setSearch(val); setPage(1); }}
          onAdd={canCreate ? () => {
            setIsEditApi(false);
            setWarningData({});
            setErrors({});
            setAddWarning(true);
          } : undefined}
        />
        <LanguageTabs value={lang} onChange={setLang} />
        {loaderForGetAll ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            rows={getAllWarning || []}
            columns={columns}
            page={page}
            count={count}
            rowsPerPage={countPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setCountPerPage}
          />
        )}
      </Paper>

      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          L&ouml;schen best&auml;tigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie diese Warnung l&ouml;schen m&ouml;chten?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">Nein</Button>
          <Button onClick={() => deleteWarningData()} color="error" variant="contained">Ja</Button>
        </DialogActions>
      </Dialog>

      {addWarning && (
        <Dialog fullScreen open={addWarning}>
          <Toolbar>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => addCompanyModelClose()}
              aria-label="close"
              sx={{ ml: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <List>
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
              {/* Dual-language column headers */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Deutsch
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    English
                  </Typography>
                </Box>
              </Box>

              {/* Titel row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Titel
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    id="warningName_ger"
                    name="warningName_ger"
                    value={warningData?.warningName_ger || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["warningName_ger"]}
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    id="warningName"
                    name="warningName"
                    value={warningData?.warningName || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["warningName"]}
                  </Typography>
                </Box>
              </Box>

              {/* Beschreibung row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Beschreibung
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    multiline
                    id="warningDetails_ger"
                    name="warningDetails_ger"
                    value={warningData?.warningDetails_ger || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["warningDetails_ger"]}
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    multiline
                    id="warningDetails"
                    name="warningDetails"
                    value={warningData?.warningDetails || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["warningDetails"]}
                  </Typography>
                </Box>
              </Box>

              {/* Datum row (shared) */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Datum
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "83.34%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    type="datetime-local"
                    name="date"
                    value={warningData?.date || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["date"]}
                  </Typography>
                </Box>
              </Box>

              {/* Ablaufdatum row (shared) */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Ablaufdatum
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "83.34%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    type="datetime-local"
                    name="expiryDate"
                    value={warningData?.expiryDate || ""}
                    onChange={(e) => onhandleChange(e)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Leer lassen f&uuml;r dauerhafte Warnung
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {loader ? (
                <Button variant="contained" type="button" disabled>
                  {isEditApi ? "Speichern" : "Hinzuf\u00fcgen"}
                  <CircularProgress size={20} sx={{ mx: 3, color: "white" }} />
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => {
                    isEditApi ? editWarningData() : addWarningData();
                  }}
                >
                  {isEditApi ? "Speichern" : "Hinzuf\u00fcgen"}
                </Button>
              )}
            </Box>
          </List>
        </Dialog>
      )}
    </>
  );
}
